import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Lightbulb, RefreshCw, Download } from 'lucide-react';
import { api } from '../context/AuthContext';

export const AdvisorIcon = Lightbulb;

const CATS = [
  { key: 'Cost', label: 'Cost', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { key: 'Security', label: 'Security', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  { key: 'Reliability', label: 'Reliability / DR', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  { key: 'Commitment', label: 'Commitments', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  { key: 'Backup', label: 'Backup', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
];
const PRI = {
  HIGH: 'bg-rose-50 text-rose-700 border-rose-200',
  MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200',
  LOW: 'bg-slate-100 text-slate-600 border-slate-200',
  INFO: 'bg-sky-50 text-sky-700 border-sky-200',
};
const usd = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const Badge = ({ cls, children }) => <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${cls}`}>{children}</span>;

export default function AwsAdvisor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cat, setCat] = useState('ALL');
  const [pri, setPri] = useState('ALL');
  const [q, setQ] = useState('');

  const load = useCallback((force) => {
    setLoading(true); setError('');
    api.get('/api/aws-inventory/advisor', { params: force ? { force: 1 } : {} })
      .then((r) => setData(r.data))
      .catch((e) => setError((e.response && e.response.data && e.response.data.error) || e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(false); }, [load]);

  const items = useMemo(() => {
    if (!data) return [];
    const s = q.trim().toLowerCase();
    return data.items.filter((i) => (cat === 'ALL' || i.category === cat) && (pri === 'ALL' || i.priority === pri))
      .filter((i) => !s || [i.title, i.resource, i.resourceName, i.detail, i.region].join(' ').toLowerCase().includes(s));
  }, [data, cat, pri, q]);

  const exportCsv = () => {
    const cols = ['priority', 'category', 'title', 'region', 'resource', 'resourceName', 'estMonthlySaving', 'detail', 'action'];
    const esc = (v) => { const x = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x; };
    const csv = [cols.join(',')].concat(data.items.map((r) => cols.map((c) => esc(r[c])).join(','))).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `aws-advisor-${(data.account && data.account.key) || 'aws'}-${data.generatedAt.slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  if (!data && loading) return <div className="p-8 text-sm text-slate-500 flex items-center gap-2"><RefreshCw className="animate-spin" style={{ width: 16, height: 16 }} /> Analysing cost, security, reliability, commitments and backup… (can take up to a minute)</div>;
  if (!data) return <div className="p-6"><div className="text-sm text-rose-600 mb-3">{error || 'No data.'}</div><button onClick={() => load(true)} className="px-3 py-1.5 text-sm rounded-lg bg-brand-600 text-white">Retry</button></div>;

  const sum = data.summary || {};
  const high = data.items.filter((i) => i.priority === 'HIGH').length;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-2"><Lightbulb style={{ width: 18, height: 18 }} className="text-brand-600" /> AWS Advisor</h2>
            <p className="text-xs text-slate-500 mt-1">
              Read-only recommendations for {data.account && data.account.label}. Regions: {(data.regions || []).join(', ')} · Generated {new Date(data.generatedAt).toLocaleString()}{data.cached ? ' (cached)' : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"><Download style={{ width: 14, height: 14 }} /> Export CSV</button>
            <button onClick={() => load(true)} disabled={loading} className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-brand-600 text-white disabled:opacity-60"><RefreshCw className={loading ? 'animate-spin' : ''} style={{ width: 14, height: 14 }} /> Re-analyse</button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-4">
          <button onClick={() => { setCat('ALL'); setPri('HIGH'); }} className="text-left rounded-lg border p-3 bg-slate-50 border-slate-200 hover:shadow-sm">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">High priority</div>
            <div className="text-xl font-semibold text-rose-600">{high}</div>
            <div className="text-[11px] text-slate-500">Est. saving {usd(data.totalEstMonthlySaving)}/mo</div>
          </button>
          {CATS.map((c) => (
            <button key={c.key} onClick={() => { setCat(c.key); setPri('ALL'); }} className={`text-left rounded-lg border p-3 hover:shadow-sm ${c.cls}`}>
              <div className="text-[11px] font-medium uppercase tracking-wide opacity-80">{c.label}</div>
              <div className="text-xl font-semibold">{(sum[c.key] && sum[c.key].count) || 0}</div>
              <div className="text-[11px] opacity-80">{(sum[c.key] && sum[c.key].high) || 0} high{sum[c.key] && sum[c.key].saving ? ` · ${usd(sum[c.key].saving)}/mo` : ''}</div>
            </button>
          ))}
        </div>
        <div className="text-[11px] text-slate-400 mt-2">Savings are rough estimates from list prices (Singapore/Jakarta), not your actual bill.</div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="flex flex-wrap gap-2 items-center p-3 border-b border-slate-100">
          {[{ key: 'ALL', label: 'All' }].concat(CATS).map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)} className={`px-2.5 py-1 rounded-full border text-xs ${cat === c.key ? 'bg-brand-600 text-white border-brand-600' : 'border-slate-200 text-slate-600'}`}>{c.label}</button>
          ))}
          <span className="mx-1 text-slate-300">|</span>
          {['ALL', 'HIGH', 'MEDIUM', 'LOW'].map((p) => (
            <button key={p} onClick={() => setPri(p)} className={`px-2.5 py-1 rounded-full border text-xs ${pri === p ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-200 text-slate-600'}`}>{p === 'ALL' ? 'Any priority' : p}</button>
          ))}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter…" className="ml-auto text-sm border border-slate-200 rounded-lg px-2.5 py-1 w-56 max-w-full" />
        </div>
        <div className="divide-y divide-slate-100">
          {items.map((i, idx) => {
            const c = CATS.find((x) => x.key === i.category) || CATS[0];
            return (
              <div key={idx} className="p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge cls={PRI[i.priority]}>{i.priority}</Badge>
                  <Badge cls={c.cls}>{c.label}</Badge>
                  <span className="text-sm font-medium text-slate-800">{i.title}</span>
                  {i.estMonthlySaving > 0 && <span className="text-xs font-semibold text-emerald-600">≈ {usd(i.estMonthlySaving)}/mo</span>}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {i.resourceName && <b className="text-slate-700">{i.resourceName} </b>}
                  {i.resource && <span className="font-mono">{i.resource}</span>}
                  {i.region && <span> · {i.region}</span>}
                </div>
                {i.detail && <div className="mt-1 text-xs text-slate-600">{i.detail}</div>}
                {i.action && <div className="mt-1 text-xs text-brand-700"><b>Action:</b> {i.action}</div>}
              </div>
            );
          })}
          {items.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No recommendations for this filter.</div>}
        </div>
      </div>
      {(data.errors || []).length > 0 && <div className="text-[11px] text-slate-400">Warnings: {data.errors.join(' · ')}</div>}
    </div>
  );
}
